import { EcoaInput } from "@components/common/EcoaInput";
import { classNames } from "@maven-helper/functions/Misc.function";
import { ReactNode, useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { fontSizes, FontSizeType } from "@styles/fontStyle";
import { CrfItemCodeValTypeTextSpec, CrfItemDirection, CrfItemUiImgAlignment } from "maven-lib/dist/consts/edc";
import { alterElementOnImgErr, StyledImgWrapper } from "./Common.styles";
import { CrIcon } from "@maven-surface/components/base/CrIcon";
import { IconPackageName } from "@maven-surface/components/interface/Icon";

interface EcoaRadioItemSpec {
  id: string;
  key: string | number | null;
  icon?: IconPackageName;
  img?: { url: string };
  slot?: ReactNode;
  slotPosition?: "TOP" | "BOTTOM" | "RIGHT";
  text: string;
  typeText?: CrfItemCodeValTypeTextSpec;
  calcVal?: string | number | null;
  isSameFontSizeForQNA?: boolean;
}
interface EcoaRadioProps {
  fontSize: "SMALL" | "MEDIUM" | "LARGE";
  item: EcoaRadioItemSpec;
  disabled?: boolean;
  values: Array<{ itemId: string | null; dbVal: string | number | null; calcVal?: string | number | null }>;
  direction?: CrfItemDirection;
  selectedValue?: string | number | null;
  onValueChange: (value: Array<{ itemId: string | null; dbVal: string | number | null; calcVal?: string | number | null }>) => void;
}

interface StyledEcoaRadioContentWrapperProps {
  imgPosition?: CrfItemUiImgAlignment;
}

interface StyledRadioItemTextProps {
  fontSize: FontSizeType;
}

const StyledEcoaRadioContentWrapper = styled.div<StyledEcoaRadioContentWrapperProps>`
  display: flex;
  gap: 5px;
  flex-basis: 100%;

  ${({ imgPosition }) => (!imgPosition || imgPosition === "BOTTOM") && "flex-direction: column;"}
  ${({ imgPosition }) => imgPosition === "TOP" && "flex-direction: column-reverse;"}
  ${({ imgPosition }) => imgPosition === "RIGHT" && "flex-direction: row;"}
  
  .--img-render-error {
    color: #a0aec0;
    .--img-render-error-icon {
      font-size: 2rem;
    }
  }
`;

const StyledRadioItemText = styled.div<StyledRadioItemTextProps>`
  ${({ fontSize }) => `font-size: ${fontSizes[fontSize] ?? "inherit"};`}
  ${({ fontSize }) => {
    switch (fontSize) {
      case "6XL":
      case "7XL":
      case "8XL":
        return `line-height: 1.3;`;
    }
  }}
`;

const StyledLegacyImageWrapper = styled.div`
  img {
    max-width: 150px;
    max-height: 100px;
  }
`;

export function EcoaRadio(props: EcoaRadioProps) {
  const {
    item: { id, key, icon, img, slot, slotPosition, text, typeText, calcVal, isSameFontSizeForQNA },
    disabled,
    values,
    selectedValue,
    fontSize,
    direction,
    onValueChange,
  } = props;
  const typeTextValue = values.find((e) => e.itemId === typeText?.id)?.dbVal;
  const [isTextFocusOn, setIsTextFocusOn] = useState<boolean>(false);

  const handleRadioValueChange = (val: string | number | null) => {
    onValueChange(values.map((item) => (item.itemId === id ? { ...item, dbVal: val, calcVal: calcVal } : item)));
  };

  const handleTypeTextValueChange = useCallback(
    (val: string | number | null) => {
      onValueChange(values.map((item) => (item.itemId === typeText?.id ? { itemId: item.itemId, dbVal: val, calcVal: calcVal } : item)));
    },
    [onValueChange, typeText?.id, values, calcVal]
  );

  useEffect(() => {
    if (isTextFocusOn) {
      setIsTextFocusOn(false);
    }
  }, [isTextFocusOn, setIsTextFocusOn]);

  useEffect(() => {
    if (typeText && typeTextValue && selectedValue !== key) {
      handleTypeTextValueChange(null);
    }
  }, [handleTypeTextValueChange, key, onValueChange, selectedValue, typeText, typeTextValue, values]);

  const itemFontSize = (() => {
    if (isSameFontSizeForQNA) {
      return fontSize === "LARGE" ? "8XL" : fontSize === "MEDIUM" ? "5XL" : "3XL";
    } else {
      if (direction === "HORIZONTAL") {
        return fontSize === "LARGE" ? "3XL" : fontSize === "MEDIUM" ? "XL" : "BASE";
      } else {
        return fontSize === "LARGE" ? "5XL" : fontSize === "MEDIUM" ? "3XL" : "LG";
      }
    }
  })();

  return (
    <div
      className={classNames("hb-radio-item", selectedValue === key && "--selected", disabled && "--disabled", !!icon && "--icon")}
      key={key}
      onClick={() => {
        if (disabled) return;

        handleRadioValueChange(key);
        setIsTextFocusOn(true);
      }}
    >
      <div>
        <div className="hb-radio-item-icon-and-text-container">
          {!!icon && (
            <span className={`hb-radio-item-icon --${icon.package.toLowerCase()}`}>
              <CrIcon icon={icon} />
            </span>
          )}
          <StyledEcoaRadioContentWrapper imgPosition={slotPosition}>
            <StyledRadioItemText className={classNames("hb-radio-item-text")} fontSize={itemFontSize}>
              {text}
            </StyledRadioItemText>
            {!!img && (
              <StyledLegacyImageWrapper>
                <StyledImgWrapper>
                  <img src={img.url} alt="" onError={(e) => (e.currentTarget.outerHTML = alterElementOnImgErr)} />
                </StyledImgWrapper>
              </StyledLegacyImageWrapper>
            )}
            {slot}
          </StyledEcoaRadioContentWrapper>
        </div>
        {typeText && (
          <EcoaInput
            isTextFocusOn={isTextFocusOn}
            defaultVal={typeTextValue}
            disabled={selectedValue !== key || disabled}
            placeholder={typeText.placeholder}
            onValueChange={(value) => {
              handleTypeTextValueChange(value);
            }}
            onClickEvent={(ev) => {
              ev.stopPropagation();
            }}
            prefix={typeText?.uiPrefix}
            suffix={typeText?.uiSuffix}
          />
        )}
      </div>
    </div>
  );
}
